// @flow
import type { NavLinkType } from './types';

/**
 * Utils to convert NavLinkType from GraphQL response
 */

export type LinkData = {
  anchorText: ?string,
  url: ?string,
  linkId: ?string,
  sublinks: ?(LinkData[]),
};

type PartialNavLinkType = {
  id: string,
  icon: ?string,
  urlWithGeoTemplate: ?string,
  urlWithLatLngTemplate: ?string,
  forceCollapse: boolean,
  isNewWindow: boolean,
  isNoFollow: boolean,
  hideFromSidebar: boolean,
  sidebarPostCollapsed: boolean,
};

const defaultData = {
  icon: null,
  urlWithGeoTemplate: null,
  urlWithLatLngTemplate: null,
  forceCollapse: false,
  isNewWindow: false,
  isNoFollow: false,
  hideFromSidebar: false,
  sidebarPostCollapsed: false,
};
/* eslint-disable no-template-curly-in-string */
const servletPrefix2partialData: Map<string, PartialNavLinkType> = new Map([
  [
    'scoped-home',
    {
      ...defaultData,
      id: 'scoped-home',
      icon: 'map-pin-fill',
    },
  ],
  [
    'hotels',
    {
      ...defaultData,
      id: 'hotels',
      icon: 'hotels',
      urlWithGeoTemplate: '/Hotels-g${geoId}',
      urlWithLatLngTemplate:
        '/MobileNearbyHotels?j=distLow&latitude=${latitude}&longitude=${longitude}',
    },
  ],
  [
    'attractions',
    {
      ...defaultData,
      id: 'attractions',
      icon: 'attractions',
      urlWithGeoTemplate: '/Attractions-g${geoId}',
      urlWithLatLngTemplate:
        '/MobileNearbyAttractions?j=distLow&latitude=${latitude}&longitude=${longitude}',
    },
  ],
  [
    'restaurants',
    {
      ...defaultData,
      id: 'restaurants',
      icon: 'restaurants',
      urlWithGeoTemplate: '/Restaurants-g${geoId}',
      urlWithLatLngTemplate:
        '/MobileNearbyRestaurants?j=distLow&latitude=${latitude}&longitude=${longitude}',
    },
  ],
  [
    'flights',
    {
      ...defaultData,
      id: 'flights',
      icon: 'flights',
    },
  ],
  [
    'vr',
    {
      ...defaultData,
      id: 'VacationRentals',
      icon: 'vacation-rentals',
      urlWithGeoTemplate: '/VacationRentals-g${geoId}',
      urlWithLatLngTemplate:
        '/MobileVacationRentalSearch?j=distLow&latitude=${latitude}&longitude=${longitude}',
    },
  ],
  [
    'shopping',
    {
      ...defaultData,
      id: 'shopping',
      icon: 'shopping-bag-fill',
    },
  ],
  [
    'vp',
    {
      ...defaultData,
      id: 'vacation-package',
      icon: 'on-the-beach',
      urlWithGeoTemplate: '/Vacation_Packages-g${geoId}',
      urlWithLatLngTemplate:
        '/MobileVacationPackagesSearch?j=distLow&latitude=${latitude}&longitude=${longitude}',
    },
  ],
  [
    'cruises',
    {
      ...defaultData,
      id: 'cruises',
      icon: 'cruises',
      promoteAsNew: true,
    },
  ],
  [
    'cars',
    {
      ...defaultData,
      id: 'cars',
      icon: 'parking',
    },
  ],
  [
    'Forums',
    {
      ...defaultData,
      id: 'Forums',
      icon: 'forums',
    },
  ],
  [
    'Airlines',
    {
      ...defaultData,
      id: 'Airlines',
      icon: 'flights',
    },
  ],
  [
    'TravelersChoice',
    {
      ...defaultData,
      id: 'TravelersChoice',
      icon: 'travelers-choice-badge',
    },
  ],
  [
    'TripInspiration',
    {
      ...defaultData,
      id: 'Inspiration',
      sidebarPostCollapsed: true,
    },
  ],
  [
    'RoadTrips',
    {
      ...defaultData,
      id: 'RoadTrip',
      sidebarPostCollapsed: true,
    },
  ],
  [
    'HelpDesk',
    {
      ...defaultData,
      id: 'HelpDesk',
      icon: 'question-circle',
      sidebarPostCollapsed: true,
    },
  ],
]);

export const convertAndPushToArray = (
  linksData: ?(LinkData[]),
  isTopLevelLink: boolean,
  forceCollapse: boolean,
  result: NavLinkType[],
) => {
  if (linksData && linksData.length) {
    linksData
      .map((linkData, index) =>
        /* eslint-disable no-use-before-define */
        convertSingle(index, linkData, isTopLevelLink, forceCollapse),
      )
      .forEach(navLinkType => {
        if (navLinkType && navLinkType.length) {
          result.push(...navLinkType);
        }
      });
  }
};

const convertSingle = (
  index: number,
  linkData: LinkData,
  isTopLevelLink: boolean,
  forceCollapse: boolean,
): NavLinkType[] => {
  const result: NavLinkType[] = [];
  if (linkData.url || linkData.anchorText) {
    const subList: NavLinkType[] = [];
    if (linkData.sublinks && linkData.sublinks.length) {
      convertAndPushToArray(linkData.sublinks, false, forceCollapse, subList);
    }
    if (linkData.url && isTopLevelLink) {
      if (linkData.url === '/') {
        result.push({
          ...defaultData,
          id: 'unscoped-home',
          icon: 'globe-world',
          trackingLabel: 'unscoped-home',
          text: linkData.anchorText || '',
          url: linkData.url,
          subList,
          forceCollapse: false,
          promoteAsNew: false,
        });
      } else {
        servletPrefix2partialData.forEach((value, key) => {
          if (linkData.linkId === key) {
            result.push({
              ...value,
              text: linkData.anchorText || '',
              trackingLabel: linkData.linkId || '',
              url: linkData.url,
              subList,
              forceCollapse: value.forceCollapse || forceCollapse,
              promoteAsNew: false,
            });
          }
        });
      }
    } else {
      result.push({
        ...defaultData,
        id: index.toString(),
        trackingLabel: linkData.linkId || '',
        text: linkData.anchorText || '',
        url: linkData.url,
        subList,
        forceCollapse: defaultData.forceCollapse || forceCollapse,
        promoteAsNew: false,
      });
    }
  } else {
    convertAndPushToArray(linkData.sublinks, isTopLevelLink, true, result);
  }
  return result;
};

const convertArray = (linksData: ?(LinkData[])): NavLinkType[] => {
  const result: NavLinkType[] = [];
  convertAndPushToArray(linksData, true, false, result);
  return result;
};

export default convertArray;
